export const MESSAGE_QUOTA_VISIBLE_FROM = 10;
export const MESSAGE_QUOTA_NEAR_LIMIT = 3;
export const MESSAGE_QUOTA_MAX_FALLBACK = 30;
export const SECONDS_PER_MINUTE = 60;
export const MINUTES_PER_HOUR = 60;
export const COMPOSER_RETRY_FALLBACK_SECONDS = 60;

export const PAUSE_REASON_HOURLY = "hourly";
export const PAUSE_REASON_CHAT_CREATE = "chat_create";
export const PAUSE_REASON_DAILY = "daily";
export const PAUSE_REASON_CAPACITY = "capacity";

export const PAUSE_TITLE_CHAT_CREATE =
  "Please wait before starting another chat";
export const PAUSE_TITLE_DAILY = "Today's message limit reached";
export const PAUSE_TITLE_CAPACITY = "Scan AI is temporarily at capacity";

export const HOURLY_SEND_AGAIN_PREFIX = "You can send again in";

export function formatRetryWaitPhrase(secondsLeft) {
  const seconds = Math.max(0, Math.ceil(Number(secondsLeft) || 0));
  if (seconds <= 0) {
    return null;
  }

  const minutes = Math.ceil(seconds / SECONDS_PER_MINUTE);
  if (minutes >= MINUTES_PER_HOUR) {
    const hours = Math.ceil(minutes / MINUTES_PER_HOUR);
    return hours === 1 ? "about 1 hour" : `about ${hours} hours`;
  }
  if (minutes <= 1) {
    return "about a minute";
  }
  return `about ${minutes} minutes`;
}

export function hourlySendAgainHelper(secondsLeft) {
  const wait = formatRetryWaitPhrase(secondsLeft);
  if (!wait) {
    return "";
  }
  return `${HOURLY_SEND_AGAIN_PREFIX} ${wait}.`;
}

export function messageQuotaLabel(remaining) {
  if (remaining === 1) {
    return "1 message left this hour";
  }
  return `${remaining} messages left this hour`;
}

export function messageQuotaNudge(remaining) {
  if (remaining === 1) {
    return "Only 1 message left this hour — share the key details so we can recommend a scan.";
  }
  return `Only ${remaining} messages left this hour — share the key details so we can recommend a scan.`;
}

export function shouldShowMessageQuota(remaining) {
  return (
    typeof remaining === "number" &&
    remaining > 0 &&
    remaining <= MESSAGE_QUOTA_VISIBLE_FROM
  );
}

export function isQuotaExhausted(remainingMessages) {
  return (
    remainingMessages !== null &&
    remainingMessages !== undefined &&
    !Number.isNaN(remainingMessages) &&
    remainingMessages <= 0
  );
}

export function isHourlyPause(reason) {
  return reason === PAUSE_REASON_HOURLY;
}

export function shouldHoldComposerBlockWithoutRetry(reason) {
  return reason === PAUSE_REASON_CAPACITY || reason === PAUSE_REASON_DAILY;
}

export function composerBlockHoldSeconds({
  retryAfterSeconds = 0,
  reason = PAUSE_REASON_HOURLY,
} = {}) {
  const requestedSeconds = Math.max(
    0,
    Math.ceil(Number(retryAfterSeconds) || 0)
  );
  if (requestedSeconds > 0) {
    return requestedSeconds;
  }
  if (reason === PAUSE_REASON_CHAT_CREATE) {
    return COMPOSER_RETRY_FALLBACK_SECONDS;
  }
  return 0;
}

export function pauseReasonFromError(error) {
  switch (error) {
    case "daily_user_exhausted":
      return PAUSE_REASON_DAILY;
    case "spend_exhausted":
    case "kill_switch":
    case "unavailable":
      return PAUSE_REASON_CAPACITY;
    case "rate_limited":
      return PAUSE_REASON_HOURLY;
    default:
      return PAUSE_REASON_HOURLY;
  }
}

export function composerPauseTitle(reason) {
  switch (reason) {
    case PAUSE_REASON_CHAT_CREATE:
      return PAUSE_TITLE_CHAT_CREATE;
    case PAUSE_REASON_DAILY:
      return PAUSE_TITLE_DAILY;
    case PAUSE_REASON_CAPACITY:
      return PAUSE_TITLE_CAPACITY;
    case PAUSE_REASON_HOURLY:
      return "";
    default:
      return "";
  }
}

export function composerLockHelper({
  reason = PAUSE_REASON_HOURLY,
  secondsLeft = 0,
} = {}) {
  if (isHourlyPause(reason)) {
    return hourlySendAgainHelper(secondsLeft);
  }
  return composerPauseTitle(reason);
}

export function quotaPauseFromPayload({
  remainingMessages,
  retryAfterSeconds,
} = {}) {
  if (!isQuotaExhausted(remainingMessages)) {
    return {
      exhausted: false,
      remainingMessages:
        remainingMessages === undefined || remainingMessages === null
          ? null
          : Number(remainingMessages),
      retryAfterSeconds: 0,
    };
  }

  return {
    exhausted: true,
    remainingMessages: 0,
    retryAfterSeconds: Math.max(0, Math.ceil(Number(retryAfterSeconds) || 0)),
  };
}
